import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import server from 'server/serverController2'
import { formatPriceByLocale, removeItem } from 'utils/utils'

import { CenterModalCallbackContext } from 'context/popup/CenterModalProvider'
import { AnimatedWindowCallbackContext } from 'context/window/AnimatedWindowProvider'

import ConfirmCenterModal from 'components/presenters/popup/ConfirmCenterModal'
import ProductOrder from 'components/containers/commerce/order/ProductOrder'

const usePurchaseModal = ({ productInfo }) => {
  const { no, type, lowest_price: lowestPrice, title, banner_img: bannerImg } = productInfo

  const userInfo = useSelector((state) => state.userInfo)

  const { openCenterModal, closeCenterModal } = useContext(CenterModalCallbackContext)
  const { openWindow } = useContext(AnimatedWindowCallbackContext)

  const whiteboxRef = useRef(null)
  const history = useHistory()
  const location = useLocation()

  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false)
  const [purchaseModalType, setPurchaseModalType] = useState('')
  const [productVariants, setProductVariants] = useState([])
  const [selectOptions, setSelectOptions] = useState([])
  const [totalPrice, setTotalPrice] = useState(0)
  const [isValid, setIsValid] = useState(false)

  const handlePurchaseModal = useCallback((visible, type) => {
    setPurchaseModalVisible(visible)
    setPurchaseModalType(type)
  }, [])

  const loadProductVariants = useMemo(async () => {
    try {
      const response = await server.getRequestData(
        `commerce/products/${no}/variants?is_show_skus=true`,
      )
      if (response.success !== 1) {
        const errData = response.error.res_data
        throw errData.detail_code
      }
      const { variants } = response.data

      return variants
    } catch (error) {
      console.log(error)
    }
  }, [no])

  const loadProductSkus = useMemo(async () => {
    try {
      const response = await server.getRequestData(`commerce/products/${no}/skus`)
      if (response.success !== 1) {
        const errData = response.error.res_data
        throw errData.detail_code
      }
      const { skus } = response.data

      return skus
    } catch (error) {
      console.log(error)
    }
  }, [no])

  const handleSetOptions = useCallback(async () => {
    const variants = await loadProductVariants
    for (const variant of variants) {
      variant.options = variant.skus.reduce((acc, sku) => {
        const option = {}
        option.no = sku.no
        option.label = sku.name
        option.value = { ...sku }

        if (sku.price > lowestPrice) {
          const extraPrice = sku.price - lowestPrice
          option.subText = `+${formatPriceByLocale('ko', extraPrice)}원`
        }
        acc = [...acc, option]
        return acc
      }, [])
    }
    setProductVariants(variants)
  }, [loadProductVariants, lowestPrice])

  const onDuplicateCheck = useCallback(
    (optionNo) => {
      for (const option of selectOptions) {
        if (optionNo === option.no) return true
      }
      return false
    },
    [selectOptions],
  )

  const onFormatSelectOption = useCallback(
    (value) => {
      const selectOption = {}
      selectOption.no = value.no
      selectOption.name = value.name
      selectOption.count = 1
      selectOption.price = value.price
      selectOption.stock = value.stock
      selectOption.challengePrice = purchaseModalType === 'challenge' ? value.challenge_price : null
      selectOption.decreaseDisable = true
      selectOption.increaseDisable = purchaseModalType === 'challenge' ? true : false
      selectOption.isChallenge = purchaseModalType === 'challenge' ? true : false

      return selectOption
    },
    [purchaseModalType],
  )

  const handleSelectOption = useCallback(
    ({ value }) => {
      const selectOption = onFormatSelectOption(value)

      if (purchaseModalType === 'challenge') {
        setSelectOptions([selectOption])
        return
      }

      const isDuplicate = selectOptions.length > 0 ? onDuplicateCheck(value.no) : false

      if (isDuplicate) return

      setSelectOptions((options) => [...options, selectOption])
    },
    [onDuplicateCheck, onFormatSelectOption, purchaseModalType, selectOptions.length],
  )

  const handleTriggerSetOption = useCallback(async () => {
    const skuData = await loadProductSkus
    const selectOption = onFormatSelectOption(skuData[0])

    setSelectOptions([selectOption])
  }, [loadProductSkus, onFormatSelectOption])

  const handleOptionDelete = useCallback(
    (idx) => {
      if (type === 'normal') {
        return
      }
      const reloadCurrOptions = removeItem(selectOptions, idx)
      setSelectOptions(reloadCurrOptions)
    },
    [selectOptions, type],
  )

  const handleClickPurchaseBtn = useCallback(
    (e) => {
      const type = e.currentTarget.getAttribute('data-type')
      handlePurchaseModal(true, type)
    },
    [handlePurchaseModal],
  )

  const handleClickCloseBtn = useCallback(() => {
    handlePurchaseModal(false, '')
    setSelectOptions([])
  }, [handlePurchaseModal])

  const handleQuantityIncrease = (optionNo, stock) => {
    const options = [...selectOptions]
    for (const option of options) {
      if (option.no === optionNo) {
        if (stock === option.count) {
          option.increaseDisable = true
          return
        }
        option.decreaseDisable = false
        option.count += 1
      }
    }
    setSelectOptions(options)
  }

  const handleQuantityDecrease = useCallback(
    (optionNo) => {
      const options = [...selectOptions]
      for (const option of options) {
        if (option.no === optionNo) {
          console.log(option.count - 1)
          if (option.count - 1 <= 0) {
            option.decreaseDisable = true
          } else {
            option.count -= 1
          }
        }
      }
      setSelectOptions(options)
    },
    [selectOptions],
  )

  const onCalcTotalPrice = useCallback(() => {
    let totalPrice = 0
    if (selectOptions.length > 0) {
      for (const option of selectOptions) {
        totalPrice += option.price * option.count
      }
    }
    setTotalPrice(totalPrice)
  }, [selectOptions])

  const handleCompleteLoginAlert = (id) => {
    closeCenterModal(id)
    history.replace(`/social?redirect_path=${location.pathname}`)
  }

  const handleClickPurchase = () => {
    handleInitPurchaseModal()

    if (userInfo.isLogin) {
      openWindow({
        Component: (
          <ProductOrder
            selectedOptions={selectOptions}
            purchaseType={purchaseModalType}
            productTitle={title}
            productImg={bannerImg}
          />
        ),
        headerProps: { title: '주문/결제' },
      })
    } else {
      const dataList = {
        title: '로그인 후 구매가 가능해요.',
        description: '로그인 하셔야 주문을 할 수 있어요',
        confirmTxt: '로그인',
      }
      openCenterModal({
        Component: (
          <ConfirmCenterModal
            dataList={dataList}
            onComplete={handleCompleteLoginAlert}
            onCancel={(id) => closeCenterModal(id)}
          />
        ),
      })
    }
  }

  const handleInitPurchaseModal = () => {
    setPurchaseModalVisible(false)
    setPurchaseModalType('')
    setProductVariants([])
    setSelectOptions([])
    setTotalPrice(0)
    setIsValid(false)
  }

  useEffect(() => {
    const handleOutSideClick = (e) => {
      if (whiteboxRef.current && !whiteboxRef.current.contains(e.target)) {
        handleInitPurchaseModal()
      }
    }

    document.addEventListener('mousedown', handleOutSideClick)
    return () => document.addEventListener('mousedown', handleOutSideClick)
  }, [handlePurchaseModal, whiteboxRef])

  // useEffect(() => {
  // 	if (purchaseModalVisible) {
  // 		if (type === "normal") {
  // 			handleTriggerSetOption()
  // 		} else {
  // 			handleSetOptions()
  // 		}
  // 	}
  // }, [purchaseModalVisible, type, handleTriggerSetOption, handleSetOptions])

  useEffect(() => {
    if (type === 'normal') {
      handleTriggerSetOption()
    } else {
      handleSetOptions()
    }
  }, [type, handleTriggerSetOption, handleSetOptions])

  useEffect(() => {
    onCalcTotalPrice()
    if (selectOptions.length > 0) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [onCalcTotalPrice, selectOptions])

  return {
    purchaseModalVisible,
    purchaseModalType,
    handleClickPurchaseBtn,
    handleClickCloseBtn,
    handleSelectOption,
    whiteboxRef,
    productVariants,
    selectOptions,
    totalPrice,
    handleOptionDelete,
    isValid,
    handleQuantityIncrease,
    handleQuantityDecrease,
    handleClickPurchase,
  }
}

export default usePurchaseModal
